import React from 'react';

type KpiTone = 'neutral' | 'positive' | 'warning' | 'danger';

type KpiCardProps = {
  label: string;
  value: string;
  helper?: string;
  tone?: KpiTone;
  compact?: boolean;
};

type StatusTone = 'neutral' | 'positive' | 'warning' | 'danger';

type StatusBadgeProps = {
  label: string;
  tone?: StatusTone;
};

type EmptyStateProps = {
  message: string;
  compact?: boolean;
};

type MetaStripProps = {
  left: string;
  right?: string;
};

type TableControlsProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: React.ReactNode;
};

const toneStyles: Record<KpiTone, string> = {
  neutral: 'border-slate-200 bg-white',
  positive: 'border-emerald-200 bg-emerald-50/40',
  warning: 'border-amber-200 bg-amber-50/40',
  danger: 'border-red-200 bg-red-50/40',
};

const badgeToneStyles: Record<StatusTone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  positive: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-700',
};

export const SectionCard: React.FC<{ title?: string; subtitle?: string; children: React.ReactNode; actions?: React.ReactNode }> = ({
  title,
  subtitle,
  children,
  actions,
}) => (
  <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
    {title ? (
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          {subtitle ? <p className="text-sm text-slate-600">{subtitle}</p> : null}
        </div>
        {actions || null}
      </div>
    ) : null}
    {children}
  </article>
);

export const KpiCard: React.FC<KpiCardProps> = ({ label, value, helper, tone = 'neutral', compact = false }) => (
  <article className={`rounded-xl border px-4 py-3 ${toneStyles[tone]}`}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</p>
    <p className={`mt-1 font-extrabold tracking-tight text-slate-900 ${compact ? 'text-2xl' : 'text-3xl'}`}>{value}</p>
    {helper ? <p className="mt-1 text-sm font-medium text-slate-600">{helper}</p> : null}
  </article>
);

export const StatusBadge: React.FC<StatusBadgeProps> = ({ label, tone = 'neutral' }) => (
  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badgeToneStyles[tone]}`}>{label}</span>
);

export const EmptyState: React.FC<EmptyStateProps> = ({ message, compact = false }) => (
  <div className={`rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm font-semibold text-slate-600 ${compact ? 'px-3 py-3' : 'px-4 py-6'}`}>
    {message}
  </div>
);

export const MetaStrip: React.FC<MetaStripProps> = ({ left, right }) => (
  <section className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <p className="font-semibold text-slate-700">{left}</p>
      {right ? <p className="font-semibold text-slate-600">{right}</p> : null}
    </div>
  </section>
);

export const TableControls: React.FC<TableControlsProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
  filters,
}) => (
  <div className="mb-3 flex flex-wrap items-center gap-2">
    <input
      value={searchValue}
      onChange={(event) => onSearchChange(event.target.value)}
      placeholder={searchPlaceholder}
      className="h-9 min-w-[220px] flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 outline-none focus:border-slate-500"
    />
    {filters || null}
  </div>
);
